import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { FormsModule } from '@angular/forms'
import { forkJoin } from 'rxjs'
import moment from 'moment'
import { ContractCoordinatorService } from '../../../core/services/contract-coordinator/contract-coordinator.service'
import { CoordinatorService } from '../../../core/services/coordinator/coordinator.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'

interface CoordenadorEntry {
  coordenadorId: number | null
  dataServico: string
  hora: string
  local: string
  nome?: string
  contato?: string
  editing?: boolean
}

@Component({
  selector: 'modal-coordinators',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
    FormsModule,
  ],
  templateUrl: './modal-coordinators.component.html',
  styleUrl: './modal-coordinators.component.scss',
})
export class ModalCoordinatorsComponent implements OnInit {
  loading = false
  trechos: { tipoTrechoId: number; tipoTrechoDescricao: string }[] = []
  pendingTrechoIds = new Set<number>()
  coordenadoresCatalogo: any[] = []
  coordenacaoMap: { [tipoTrechoId: number]: CoordenadorEntry[] } = {}
  savingCoordenadores: { [tipoTrechoId: number]: boolean } = {}
  addingNew: { [tipoTrechoId: number]: boolean } = {}
  novoEntry: { [tipoTrechoId: number]: CoordenadorEntry } = {}

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { contratoId: string },
    private dialogRef: MatDialogRef<ModalCoordinatorsComponent>,
    private contratoCoordenadorService: ContractCoordinatorService,
    private coordenadorService: CoordinatorService,
    private snackbar: SnackbarCustomService,
  ) {}

  ngOnInit() {
    this.load()
    this.loadCatalogo()
  }

  load() {
    this.loading = true
    forkJoin({
      ativos: this.contratoCoordenadorService.getByContrato(this.data.contratoId),
      necessidade: this.contratoCoordenadorService.getNecessidade(this.data.contratoId),
    }).subscribe({
      next: (results: any) => {
        const ativosData: any[] = results.ativos.data || []
        const necessidadeData = results.necessidade.data

        this.trechos = []
        this.coordenacaoMap = {}
        this.pendingTrechoIds = new Set<number>()
        this.addingNew = {}
        this.novoEntry = {}

        for (const trecho of ativosData) {
          this.trechos.push({ tipoTrechoId: trecho.tipoTrechoId, tipoTrechoDescricao: trecho.tipoTrechoDescricao })
          this.coordenacaoMap[trecho.tipoTrechoId] = (trecho.coordenadores || []).map((c: any) => ({
            coordenadorId: c.coordenadorId,
            dataServico: this.toInputDate(c.dataServico),
            hora: (c.hora || '').substring(0, 5),
            local: c.local || '',
            nome: c.nome,
            contato: c.contato,
            editing: false,
          }))
        }

        if (necessidadeData?.necessitaCoordenador) {
          for (const t of necessidadeData.tiposTrecho || []) {
            if (this.coordenacaoMap[t.tipoTrechoId] === undefined) {
              this.trechos.push(t)
              this.coordenacaoMap[t.tipoTrechoId] = []
            }
            this.pendingTrechoIds.add(t.tipoTrechoId)
          }
        }
      },
      error: () => { this.loading = false },
      complete: () => { this.loading = false },
    })
  }

  loadCatalogo() {
    this.coordenadorService.listCoordenador('S').subscribe({
      next: (r: any) => { this.coordenadoresCatalogo = r.data || [] },
      error: () => {},
    })
  }

  private toInputDate(value: string | null | undefined): string {
    if (!value) return ''
    const m = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
    return m.isValid() ? m.format('YYYY-MM-DD') : ''
  }

  private displayDate(value: string): string {
    if (!value) return ''
    const m = moment(value, 'YYYY-MM-DD', true)
    return m.isValid() ? m.format('DD/MM/YYYY') : value
  }

  formatEntryData(entry: CoordenadorEntry): string {
    return this.displayDate(entry.dataServico)
  }

  editarCoordenador(tipoTrechoId: number, index: number) {
    const entry = this.coordenacaoMap[tipoTrechoId][index]
    if (entry) entry.editing = true
  }

  concluirEdicao(tipoTrechoId: number, index: number) {
    const entry = this.coordenacaoMap[tipoTrechoId][index]
    if (!entry) return
    const catalogo = this.coordenadoresCatalogo.find(c => c.id === entry.coordenadorId)
    if (catalogo) {
      entry.nome = catalogo.nome
      entry.contato = catalogo.contato ?? catalogo.telefone ?? entry.contato
    }
    entry.editing = false
  }

  removerCoordenador(tipoTrechoId: number, index: number) {
    this.coordenacaoMap[tipoTrechoId] = this.coordenacaoMap[tipoTrechoId].filter((_, i) => i !== index)
  }

  mostrarFormNovo(tipoTrechoId: number) {
    this.novoEntry[tipoTrechoId] = { coordenadorId: null, dataServico: '', hora: '', local: '' }
    this.addingNew[tipoTrechoId] = true
  }

  cancelarNovo(tipoTrechoId: number) {
    this.addingNew[tipoTrechoId] = false
    delete this.novoEntry[tipoTrechoId]
  }

  confirmarNovo(tipoTrechoId: number) {
    const entry = this.novoEntry[tipoTrechoId]
    if (!entry || entry.coordenadorId == null) return
    const catalogo = this.coordenadoresCatalogo.find(c => c.id === entry.coordenadorId)
    entry.nome = catalogo?.nome
    entry.contato = catalogo?.contato ?? catalogo?.telefone
    entry.editing = false
    if (!this.coordenacaoMap[tipoTrechoId]) this.coordenacaoMap[tipoTrechoId] = []
    this.coordenacaoMap[tipoTrechoId] = [...this.coordenacaoMap[tipoTrechoId], entry]
    this.cancelarNovo(tipoTrechoId)
  }

  nomeCoordenador(coordenadorId: number | null): string {
    if (coordenadorId == null) return ''
    return this.coordenadoresCatalogo.find(c => c.id === coordenadorId)?.nome || ''
  }

  definir(tipoTrechoId: number) {
    const entradas = (this.coordenacaoMap[tipoTrechoId] || []).filter(e => e.coordenadorId != null)
    this.savingCoordenadores[tipoTrechoId] = true
    this.contratoCoordenadorService.definir({
      contratoId: this.data.contratoId,
      tipoTrechoId,
      coordenadores: entradas.map(e => ({
        coordenadorId: e.coordenadorId as number,
        dataServico: e.dataServico ? moment(e.dataServico, 'YYYY-MM-DD').format('DD/MM/YYYY') : null,
        hora: e.hora || null,
        local: e.local || null,
      })),
    }).subscribe({
      next: () => {
        this.snackbar.success('Coordenadores definidos!')
        this.load()
      },
      error: (err: any) => {
        this.snackbar.errorRequest(err)
        this.savingCoordenadores[tipoTrechoId] = false
      },
      complete: () => { this.savingCoordenadores[tipoTrechoId] = false },
    })
  }

  compareById(a: any, b: any): boolean {
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    return Number(a) === Number(b)
  }

  close() {
    this.dialogRef.close()
  }
}
